import { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link } from "react-router";
import { Search, Plus, SlidersHorizontal } from "lucide-react";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import Spinner from "../../../components/common/spinner/Spinner";
import RowList from "../../components/listing/RowList";
import Pagination from "../../components/pagination/Pagination";
import PropertyFilter from "./PropertyFilter";
import "./PropertyListComponent.css";

const PropertyListComponent = ({
  route,
  method,
  requiresAuth,
  apiPath,
  mySpace = false,
  selectedValue = null,
}) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const assetUrl = process.env.REACT_APP_ASSET_URL;
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProperties, setFilteredProperties] = useState([]);

  // ── Data fetching ────────────────────────────────────────────────────────

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      let response;

      if (method === "GET") {
        response = await axios.get(`${baseUrl}${apiPath}`);
      } else {
        response = await axios.post(
          `${baseUrl}${apiPath}`,
          {
            keyboard_search: "",
            page_number: currentPage,
            page_size: 20,
            order_by: "ABC",
            ...(mySpace && { search_type: selectedValue }),
          },
          requiresAuth
            ? { headers: { Authorization: `Bearer ${token}` } }
            : undefined
        );
      }

      if (response.status === 200 && response.data.statusType === "SUCCESS") {
        setProperties(response.data.properties || []);
        setTotalPages(response.data.totalPage || 1);
      } else {
        showToast("Failed to fetch property data", { type: "error" });
      }
    } catch (error) {
      setIsError(true);
      showToast("Error fetching property data", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async (itemId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/property/deleteProperty/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.statusType === "SUCCESS") {
        setProperties((prev) => prev.filter((item) => item.id !== itemId));
        showToast("Property deleted successfully", { type: "success" });
      } else {
        showToast(response.data.text, { type: "error" });
      }
    } catch (error) {
      console.error("Error deleting property:", error);
      showToast("Error occurred during deletion", { type: "error" });
    }
  };

  // ── Follow / unfollow ────────────────────────────────────────────────────

  const handleFollowOption = async (id, isFavorite, favoriteId) => {
    if (isFavorite && favoriteId) {
      try {
        const res = await axios.delete(
          `${baseUrl}/property/favorite/delete/${favoriteId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data.statusType === "SUCCESS") {
          showToast("Property removed from favourites", { type: "success" });
          setProperties((prev) =>
            prev.map((prop) =>
              prop.id === id
                ? { ...prop, is_favorite: false, favorite: false }
                : prop
            )
          );
        } else {
          showToast("Error removing property from favourites", { type: "error" });
        }
      } catch {
        showToast("Error removing property from favourites", { type: "error" });
      }
    } else {
      try {
        const res = await axios.post(
          `${baseUrl}/property/favorite/add`,
          { property_id: id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data.statusType === "SUCCESS") {
          showToast("Property added to favourites", { type: "success" });
          setProperties((prev) =>
            prev.map((prop) =>
              prop.id === id
                ? { ...prop, is_favorite: true, favorite: true }
                : prop
            )
          );
        } else {
          showToast("Error adding property to favourites", { type: "error" });
        }
      } catch {
        showToast("Error adding property to favourites", { type: "error" });
      }
    }
  };

  // ── Share / unshare ──────────────────────────────────────────────────────

  const handleShareToggle = async (id, isShared) => {
    try {
      const response = await axios.post(
        `${baseUrl}/property/shared`,
        { property_id: id, is_shared: !isShared },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.statusType === "SUCCESS") {
        showToast(
          !isShared ? "Property shared successfully" : "Property unshared",
          { type: "success" }
        );
        setProperties((prev) =>
          prev.map((prop) =>
            prop.id === id ? { ...prop, is_shared: !isShared } : prop
          )
        );
      } else {
        showToast("Failed to update share status", { type: "error" });
      }
    } catch {
      showToast("Error updating share status", { type: "error" });
    }
  };

  // ── Publish / unpublish ──────────────────────────────────────────────────

  const handleToggleEnable = async (itemId) => {
    const current = properties.find((p) => p.id === itemId);
    const newPublishStatus = !current.is_published;

    try {
      const response = await axios.put(
        `${baseUrl}/property/publish/status`,
        { lead_id: itemId, is_publish: newPublishStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response?.data?.statusType === "SUCCESS") {
        setProperties((prev) =>
          prev.map((prop) =>
            prop.id === itemId
              ? { ...prop, is_published: newPublishStatus }
              : prop
          )
        );
        showToast(
          newPublishStatus
            ? "Property published successfully"
            : "Property unpublished successfully",
          { type: "success" }
        );
      } else {
        showToast("Failed to update property status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating property status:", error);
      showToast("Error updating property status", { type: "error" });
    }
  };

  // ── Search ───────────────────────────────────────────────────────────────

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (!term) {
      setFilteredProperties([]);
      return;
    }

    setFilteredProperties(
      properties.filter((property) => {
        const fields = [
          property.customer?.first_name,
          property.customer?.last_name,
          property.title,
          property.description,
          property.area?.name,
          property.city?.name,
          property.state?.name,
          property.country?.name,
        ];
        return fields.some((f) => f?.toLowerCase().includes(term));
      })
    );
  };

  // ── Custom filters ───────────────────────────────────────────────────────

  const applyCustomFilters = (filters) => {
    setFilteredProperties(
      properties.filter((prop) => {
        const match = (filterKey, propKey) =>
          !filters[filterKey] ||
          String(prop[propKey]?.id) === String(filters[filterKey]);

        const matchesSearch =
          !searchTerm ||
          [
            prop.customer?.first_name,
            prop.customer?.last_name,
            prop.title,
            prop.description,
          ].some((f) => f?.toLowerCase().includes(searchTerm));

        return (
          match("purpose", "purpose") &&
          match("state", "state") &&
          match("city", "city") &&
          match("area", "area") &&
          match("type", "type") &&
          match("subType", "sub_type") &&
          match("subSubType", "sub_sub_type") &&
          matchesSearch
        );
      })
    );
  };

  // ── Pagination ───────────────────────────────────────────────────────────

  const handlePageChange = (page) => setCurrentPage(page);

  // ── Filter option helpers ─────────────────────────────────────────────────

  const getUniqueOptions = (arr, path) => {
    const seen = new Set();
    return arr
      .map((item) => {
        const value = path
          .split(".")
          .reduce((o, k) => (o ? o[k] : undefined), item);
        return value ? { id: value.id, name: value.name } : null;
      })
      .filter((opt) => {
        if (opt && !seen.has(opt.id)) {
          seen.add(opt.id);
          return true;
        }
        return false;
      });
  };

  const purposes    = getUniqueOptions(properties, "purpose");
  const states      = getUniqueOptions(properties, "state");
  const cities      = getUniqueOptions(properties, "city");
  const areas       = getUniqueOptions(properties, "area");
  const types       = getUniqueOptions(properties, "type");
  const subTypes    = getUniqueOptions(properties, "sub_type");
  const subSubTypes = getUniqueOptions(properties, "sub_sub_type");

  const displayList =
    filteredProperties.length > 0 || searchTerm
      ? filteredProperties
      : properties;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Notification />

      <div className="plc">
        {/* ── Toolbar ── */}
        <div className="plc__toolbar">
          {/* Search */}
          <div className="plc__search-wrap">
            <Search className="plc__search-icon" size={16} />
            <input
              className="plc__search-input"
              placeholder="Search by name, city, state, country…"
              value={searchTerm}
              onChange={handleSearch}
            />
            <button className="plc__search-btn" type="button">
              Search
            </button>
          </div>

          {/* Filter + Add */}
          <div className="plc__toolbar-actions">
            <PropertyFilter
              purposes={purposes}
              states={states}
              cities={cities}
              areas={areas}
              types={types}
              subTypes={subTypes}
              subSubTypes={subSubTypes}
              onFilter={applyCustomFilters}
            />

            <Link to="/add-property" className="plc__add-btn">
              <Plus size={16} />
              <span>Add Property</span>
            </Link>
          </div>
        </div>

        {/* ── List ── */}
        <div className="plc__list">
          {isLoading ? (
            <Spinner loading={true} />
          ) : displayList.length === 0 ? (
            <div className="plc__empty">
              <SlidersHorizontal size={36} className="plc__empty-icon" />
              <p className="plc__empty-title">No properties found</p>
              <p className="plc__empty-sub">
                Try adjusting your search or filters.
              </p>
            </div>
          ) : (
            displayList.map((item) => (
              <RowList
                key={item.id}
                title={item.title}
                creator={`${item.customer?.first_name || ""} ${
                  item.customer?.last_name || ""
                }`}
                CardImg={
                  item?.images?.[0]?.image_url
                    ? assetUrl + item.images[0].image_url
                    : null
                }
                createOn={item.purpose?.created_on}
                item={item}
                handleDelete={handleDelete}
                handleToggleEnable={handleToggleEnable}
                route={route}
                checkBox={item.is_published}
                isFollowing={item.is_favorite || item.favorite}
                onToggleFollow={() =>
                  handleFollowOption(item.id, item.is_favorite, item.favorite_id)
                }
                isShared={item.is_shared || false}
                onToggleShare={() =>
                  handleShareToggle(item.id, item.is_shared)
                }
              />
            ))
          )}
        </div>

        {/* ── Pagination ── */}
        {!isLoading && displayList.length > 0 && (
          <div className="plc__pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default PropertyListComponent;